import { useEffect, useRef, useState } from 'react';
import { PharmacyLayout } from './PharmacyLayout';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  Plus,
  Search,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Sparkles,
} from 'lucide-react';

interface LookupItem {
  id: string;
  name: string;
  code?: string;
  description?: string;
  isActive?: boolean;
  contact?: string;
  location?: string;
  [key: string]: any;
}

interface PharmacyLookupPageProps {
  title: string;
  description: string;
  icon: React.ElementType;
  items: LookupItem[];
  isLoading: boolean;
  error: any;
  search: string;
  setSearch: (value: string) => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  editingItem: LookupItem | null;
  setEditingItem: (item: LookupItem | null) => void;
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onDelete: (id: string) => void;
  isSubmitting: boolean;
  isDeleting: boolean;
  extraFields?: {
    name: string;
    label: string;
    type?: 'text' | 'textarea';
    placeholder?: string;
  }[];
  /** Returns a generated code from the entered name. Enables the "Generate" button on the code field. */
  codeGenerator?: (name: string) => string;
  /** Bulk action: generate codes for the given selected items. Enables the bulk "Generate Codes" button. */
  onBulkGenerateCodes?: (items: LookupItem[]) => void | Promise<void>;
  isBulkGenerating?: boolean;
  /** Bulk action: delete the given selected items. Enables the bulk "Delete" button. */
  onBulkDelete?: (items: LookupItem[]) => void | Promise<void>;
  isBulkDeleting?: boolean;
  showActiveToggle?: boolean;
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  itemName?: string;
}

export function PharmacyLookupPage({
  title,
  description,
  icon: Icon,
  items,
  isLoading,
  error,
  search,
  setSearch,
  modalOpen,
  setModalOpen,
  editingItem,
  setEditingItem,
  formData,
  setFormData,
  onSubmit,
  onDelete,
  isSubmitting,
  isDeleting,
  extraFields = [],
  codeGenerator,
  onBulkGenerateCodes,
  isBulkGenerating = false,
  onBulkDelete,
  isBulkDeleting = false,
  showActiveToggle = true,
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  itemName = 'items',
}: PharmacyLookupPageProps) {
  // Build the form payload from only the fields this page renders, so entities
  // that don't support a given field (e.g. categories have no `contact`/
  // `location`, generic names have no `code`) never submit it to the API.
  const buildFormData = (item: LookupItem | null) => {
    const data: Record<string, any> = {
      name: item?.name ?? '',
    };
    if (showActiveToggle) {
      data.isActive = item?.isActive ?? true;
    }
    extraFields.forEach((field) => {
      data[field.name] = item?.[field.name] ?? '';
    });
    return data;
  };

  const openAdd = () => {
    setEditingItem(null);
    setFormData(buildFormData(null));
    setModalOpen(true);
  };

  const openEdit = (item: LookupItem) => {
    setEditingItem(item);
    setFormData(buildFormData(item));
    setModalOpen(true);
  };

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Drop selected ids that are no longer visible (e.g. after paging/searching).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(items.map((item) => item.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [items]);

  const allSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const someSelected = items.some((item) => selectedIds.has(item.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = items.length > 0 && items.every((item) => prev.has(item.id));
      return everySelected ? new Set() : new Set(items.map((item) => item.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkGenerate = async () => {
    if (!onBulkGenerateCodes) return;
    const selected = items.filter((item) => selectedIds.has(item.id));
    await onBulkGenerateCodes(selected);
    setSelectedIds(new Set());
  };

  // Delete confirmation popup (single row action + bulk action)
  const [confirmState, setConfirmState] = useState<{
    mode: 'single' | 'bulk';
    items: LookupItem[];
  } | null>(null);

  const requestDelete = (item: LookupItem) => {
    setConfirmState({ mode: 'single', items: [item] });
  };

  const requestBulkDelete = () => {
    if (!onBulkDelete) return;
    const selected = items.filter((item) => selectedIds.has(item.id));
    if (selected.length === 0) return;
    setConfirmState({ mode: 'bulk', items: selected });
  };

  const handleConfirmDelete = async () => {
    if (!confirmState) return;
    const { mode, items: targets } = confirmState;
    try {
      if (mode === 'single') {
        await onDelete(targets[0].id);
      } else if (onBulkDelete) {
        await onBulkDelete(targets);
        setSelectedIds(new Set());
      }
    } catch {
      // errors are surfaced by the mutation's global handler
    } finally {
      setConfirmState(null);
    }
  };

  const confirmItems = confirmState?.items || [];
  const confirmIsBulk = confirmState?.mode === 'bulk';
  const bulkNoun =
    confirmItems.length === 1 ? 'selected item' : `selected ${itemName}`;

  const bulkBusy = isBulkGenerating || isBulkDeleting;
  const selectable = Boolean(onBulkGenerateCodes || onBulkDelete);
  const showBulkBar = selectable && selectedIds.size > 0;

  return (
    <PharmacyLayout
      title={title}
      description={description}
      action={
        <button
          onClick={openAdd}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
        >
          <Plus size={16} />
          Add {title.replace('Management', '').trim()}
        </button>
      }
    >
      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {showBulkBar && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            {onBulkGenerateCodes && codeGenerator && (
              <button
                type="button"
                onClick={handleBulkGenerate}
                disabled={bulkBusy}
                className="px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
              >
                {isBulkGenerating ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                Generate Codes
              </button>
            )}
            {onBulkDelete && (
              <button
                type="button"
                onClick={requestBulkDelete}
                disabled={bulkBusy}
                className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
              >
                {isBulkDeleting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkBusy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                {selectable && (
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={items.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                {extraFields.some((f) => f.name === 'code') && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                )}
                {extraFields.some((f) => f.name === 'contact') && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Contact</th>
                )}
                {extraFields.some((f) => f.name === 'location') && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Location</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                {showActiveToggle && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                )}
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={selectable ? 8 : 7} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={selectable ? 8 : 7} className="px-4 py-8">
                    <div className="flex items-center justify-center gap-2 text-danger">
                      <AlertCircle size={18} />
                      <span>Failed to load {title.toLowerCase()}</span>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={selectable ? 8 : 7} className="px-4 py-12 text-center text-text-muted">
                    {search ? (
                      <span>
                        No results match your search.{' '}
                        <button onClick={() => setSearch('')} className="text-primary hover:underline">
                          Clear search
                        </button>
                      </span>
                    ) : (
                      `No ${title.toLowerCase()} found. Add your first entry to get started.`
                    )}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-background/50 transition-colors">
                    {selectable && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleOne(item.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-text">{item.name}</td>
                    {extraFields.some((f) => f.name === 'code') && (
                      <td className="px-4 py-3 font-mono text-text-muted">{item.code || '—'}</td>
                    )}
                    {extraFields.some((f) => f.name === 'contact') && (
                      <td className="px-4 py-3 text-text-muted">{item.contact || '—'}</td>
                    )}
                    {extraFields.some((f) => f.name === 'location') && (
                      <td className="px-4 py-3 text-text-muted">{item.location || '—'}</td>
                    )}
                    <td className="px-4 py-3 text-text-muted max-w-xs truncate">{item.description || '—'}</td>
                    {showActiveToggle && (
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                          }`}
                        >
                          {item.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => requestDelete(item)}
                          disabled={isDeleting}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {onPageChange && totalPages !== undefined && totalPages > 1 && (
          <Pagination
            page={page || 1}
            totalPages={totalPages}
            total={total || 0}
            limit={limit || 20}
            onPageChange={onPageChange}
            itemName={itemName}
          />
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon size={18} className="text-primary" />
                <h2 className="text-lg font-semibold text-text">
                  {editingItem ? 'Edit' : 'Add'} {title.replace('Management', '').trim()}
                </h2>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  placeholder="Enter name"
                />
              </div>
              {extraFields.map((field) => (
                <div key={field.name}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-text">{field.label}</label>
                    {field.name === 'code' && codeGenerator && (
                      <button
                        type="button"
                        onClick={() => {
                          const generated = codeGenerator(formData.name || '');
                          if (generated) setFormData({ ...formData, code: generated });
                        }}
                        className="text-xs font-medium text-primary hover:text-primary-dark transition-colors flex items-center gap-1"
                        title="Generate code from name"
                      >
                        <Sparkles size={12} />
                        Generate
                      </button>
                    )}
                  </div>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder={field.placeholder}
                      rows={3}
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              ))}
              {showActiveToggle && (
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="lookup-isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <label htmlFor="lookup-isActive" className="text-sm text-text cursor-pointer">
                    Active
                  </label>
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                  {editingItem ? <Pencil size={16} /> : <Plus size={16} />}
                  {editingItem ? 'Save Changes' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation popup */}
      <ConfirmDialog
        open={confirmState !== null}
        title={confirmIsBulk ? 'Delete selected items' : 'Delete item'}
        description={
          confirmIsBulk ? (
            `Delete ${confirmItems.length} ${bulkNoun}? This cannot be undone.`
          ) : (
            <>
              Delete <strong>{confirmItems[0]?.name}</strong>? This cannot be undone.
            </>
          )
        }
        loading={isDeleting || isBulkDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmState(null)}
      />
    </PharmacyLayout>
  );
}
